import { createBrowserRouter, createHashRouter, Navigate, type RouteObject } from 'react-router-dom'
import { LocaleLayout } from './components/layout/LocaleLayout'
import { DatesStep } from './features/dates/DatesStep'
import { RateStep } from './features/rate/RateStep'
import { InfoStep } from './features/info/InfoStep'
import { PaymentStep } from './features/payment/PaymentStep'
import { SummaryStep } from './features/summary/SummaryStep'
import { AuthScreen } from './features/account/AuthScreen'
import { MyBookings } from './features/account/MyBookings'
import { ProfilePage } from './features/account/ProfilePage'
import { RegisterPage } from './features/account/RegisterPage'
import { HomeScreen } from './features/home/HomeScreen'
import { CouponsPage } from './features/coupons/CouponsPage'
import { FaqPage } from './features/info/FaqPage'
import { ContactsPage } from './features/info/ContactsPage'
import { DataDeletionPage } from './features/legal/DataDeletionPage'
import { PrivacyPage } from './features/legal/PrivacyPage'
import { CancelReservationPage } from './features/cancel/CancelReservationPage'
import { isNativePlatform } from './lib/platform'
import i18n, { SUPPORTED_LOCALES } from './i18n'
import type { Locale } from './lib/api/types'

/**
 * Entry redirect. The app always opens at "/" (web) or "#/" (native), so sending it to a fixed
 * locale did not merely ignore the visitor's language: LocaleLayout persists whatever locale the
 * URL carries, so the hardcoded target overwrote the stored choice on every launch. i18n has
 * already resolved the locale (URL, then stored choice, then browser) by the time this renders.
 */
function LocaleHome() {
  const lang = i18n.language as Locale
  return <Navigate to={`/${SUPPORTED_LOCALES.includes(lang) ? lang : 'it'}`} replace />
}

const routes: RouteObject[] = [
  { path: '/', element: <LocaleHome /> },
  {
    path: '/:locale',
    element: <LocaleLayout />,
    children: [
      // home/landing is now the main page; the booking funnel starts at /book
      { index: true, element: <HomeScreen /> },
      { path: 'book', element: <DatesStep /> },
      // /home kept as an alias → main page
      { path: 'home', element: <Navigate to=".." replace relative="path" /> },
      { path: 'rate', element: <RateStep /> },
      // services merged into the rate step — redirect any old deep links
      { path: 'services', element: <Navigate to="../rate" replace relative="path" /> },
      { path: 'info', element: <InfoStep /> },
      { path: 'payment', element: <PaymentStep /> },
      { path: 'summary', element: <SummaryStep /> },
      // member account
      { path: 'login', element: <AuthScreen /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'bookings', element: <MyBookings /> },
      { path: 'profile', element: <ProfilePage /> },
      // content pages (mirroring the real site)
      { path: 'coupons', element: <CouponsPage /> },
      { path: 'faq', element: <FaqPage /> },
      { path: 'contacts', element: <ContactsPage /> },
      { path: 'data-deletion', element: <DataDeletionPage /> },
      { path: 'privacy', element: <PrivacyPage /> },
      // public cancel-by-codes page (no login) — mirrors the legacy /it/cancelReservation
      { path: 'cancelReservation', element: <CancelReservationPage /> },
    ],
  },
  { path: '*', element: <LocaleHome /> },
]

// HashRouter in a Capacitor/native webview (no server rewrites); BrowserRouter on web.
export const router = isNativePlatform() ? createHashRouter(routes) : createBrowserRouter(routes)
